import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Rocket,
  CheckCircle,
  Clock,
  Award,
  TrendingUp,
  ArrowRight,
  Calendar,
  DollarSign,
  Star
} from "lucide-react";

const StaffAugmentation = () => {
  const navigate = useNavigate();
  
  const handleFindTalent = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const handleViewProfiles = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const benefits = [
    {
      icon: Clock,
      title: "Quick Onboarding",
      description: "Get skilled professionals onboard within 48-72 hours"
    },
    {
      icon: DollarSign,
      title: "Cost Effective",
      description: "Save up to 50% on hiring and operational costs"
    },
    {
      icon: TrendingUp,
      title: "Scalable Teams",
      description: "Scale your team up or down based on project needs"
    },
    {
      icon: Award,
      title: "Pre-vetted Talent",
      description: "All professionals are thoroughly screened and tested"
    },
    {
      icon: Users,
      title: "Dedicated Resources",
      description: "Full-time dedicated professionals for your projects"
    },
    {
      icon: Rocket,
      title: "Faster Delivery",
      description: "Accelerate project timelines with experienced teams"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Requirement Analysis",
      description: "We analyze your specific skill requirements and project needs"
    },
    {
      step: "02",
      title: "Talent Matching",
      description: "Our team identifies the best-fit professionals from our talent pool"
    },
    {
      step: "03",
      title: "Interview & Selection",
      description: "You interview and select the candidates that match your criteria"
    },
    {
      step: "04",
      title: "Quick Onboarding",
      description: "Selected professionals are onboarded and integrated into your team"
    }
  ];

  const testimonials = [
    {
      name: "John Anderson",
      role: "VP Engineering",
      company: "TechCorp Inc.",
      content: "Their staff augmentation helped us scale our development team quickly. The quality of developers exceeded our expectations.",
      rating: 5
    },
    {
      name: "Maria Garcia",
      role: "CTO",
      company: "StartupXYZ",
      content: "We were able to launch our product 3 months earlier thanks to the skilled developers they provided. Outstanding service!",
      rating: 5
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-duo-gradient bg-clip-text text-transparent pb-2">
            Staff Augmentation
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Scale your development team with our pre-vetted, skilled professionals seamlessly integrated into your workflow
          </p>
        </div>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
          <div className="text-center">
            <div className="text-3xl font-bold text-foreground mb-2">49+</div>
            <div className="text-muted-foreground text-sm">Available Experts</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-foreground mb-2">48hrs</div>
            <div className="text-muted-foreground text-sm">Quick Onboarding</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-foreground mb-2">95%</div>
            <div className="text-muted-foreground text-sm">Client Satisfaction</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-foreground mb-2">50%</div>
            <div className="text-muted-foreground text-sm">Cost Savings</div>
          </div>
        </div>





        {/* Testimonials */}
        <div className="mb-8">
          <h3 className="text-3xl font-bold text-center mb-8 text-foreground">
            What Our Clients Say
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="bg-gradient-card border-0 shadow-card"
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-warning text-warning" />
                    ))}
                  </div>
                  <p className="text-foreground mb-6 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <h4 className="font-semibold text-foreground">{testimonial.name}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    <p className="text-sm text-primary font-medium">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Engagement Models */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-12 text-foreground">
            Flexible Engagement Models
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300">
              <CardHeader className="text-center">
                <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Short-term</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-6">
                  3-6 months engagement for specific projects or peak workload periods.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Minimum 3 months</li>
                  <li>• Quick scaling</li>
                  <li>• Flexible terms</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300">
              <CardHeader className="text-center">
                <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Long-term</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-6">
                  12+ months engagement for ongoing development and maintenance.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Cost effective</li>
                  <li>• Deep integration</li>
                  <li>• Dedicated resources</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300">
              <CardHeader className="text-center">
                <div className="bg-gradient-primary p-4 rounded-full w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Hybrid</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-6">
                  Combination of on-site and remote resources for maximum flexibility.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Best of both worlds</li>
                  <li>• Cultural alignment</li>
                  <li>• Optimal collaboration</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

      </div>
    </section>
  );
};

export default StaffAugmentation;